/**
 * A class that manages the tree state and handles state change notifications for subscribed listeners.
 */
export class TreeStateService {
    constructor() {
        this.state = emptyState();
        this.listeners = {
            frozenColumnCount: [],
            documentDirection: [],
            columnCount: [],
        };
        this.stateInitialized = {
            frozenColumnCount: false,
            documentDirection: false,
            columnCount: false,
        };
    }
    /**
     * Adds a listener for a specific tree state property.
     * @param {K} key - The key of the tree state property.
     * @param {TreeStateListener<K>} listener - The listener function to be called when the state property changes.
     * @throws {Error} Will throw an error if the provided key is not a valid tree state property key.
     * @throws {Error} Will throw an error if the provided listener is already present for the specified key.
     */
    addListener(key, listener) {
        this.checkIfKeyIsValid(key);
        this.checkIfListenerIsNotPresent(key, listener);
        if (this.stateInitialized[key]) {
            listener(this.state[key]);
        }
        this.listeners[key].push(listener);
    }
    /**
     * Checks if a listener is already present for a specific tree state property.
     * @param key - The key of the tree state property.
     * @param listener - The listener function to be checked.
     */
    hasListener(key, listener) {
        this.checkIfKeyIsValid(key);
        return this.listeners[key].includes(listener);
    }
    checkIfKeyIsValid(key) {
        if (!(key in this.listeners)) {
            throw new Error(`Key ${key} is not a valid key`);
        }
    }
    checkIfListenerIsNotPresent(key, listener) {
        if (this.hasListener(key, listener)) {
            throw new Error(`Given listener for ${key} was already added`);
        }
    }
    /**
     * Removes a specific listener for a tree state property.
     * @param {K} key - The key of the tree state property.
     * @param {TreeStateListener<K>} listener - The listener function to be removed.
     */
    removeListener(key, listener) {
        if (key in this.listeners) {
            this.listeners[key] = this.listeners[key].filter(l => l !== listener);
        }
    }
    /**
     * Clears all listeners for the frozenColumnCount property.
     */
    clearListeners() {
        this.listeners.frozenColumnCount = [];
    }
    /**
     * Publishes a new value for a specific tree state property and notifies all listeners.
     * @param {K} key - The key of the tree state property.
     * @param {TreeState[K]} value - The new value for the tree state property.
     * @throws {Error} Will throw an error if the provided key is not a valid tree state property key.
     */
    publish(key, value) {
        this.checkIfKeyIsValid(key);
        this.state[key] = value;
        this.listeners[key].forEach(l => l(value));
        this.stateInitialized[key] = true;
    }
}
/**
 * Creates an empty tree state object with default values.
 * @returns {TreeState} An empty tree state object.
 */
function emptyState() {
    return {
        frozenColumnCount: 0,
        documentDirection: 'ltr',
        columnCount: 0,
    };
}
